<template>
  <div class="container">
    <header>
      <h1><i class="ri-gift-line"></i> {{ year }} 年度总结</h1>
    </header>

    <!-- 快捷条 -->
    <div class="back-bar">
      <a class="back-link" @click="goStats"><i class="ri-arrow-left-line"></i> 学习统计</a>
      <a class="back-link" @click="goHome"><i class="ri-book-2-line"></i> 学习笔记</a>
    </div>

    <div v-if="loading" class="loading">加载中…</div>
    <div v-else-if="error" class="error-tip">{{ error }}</div>
    <template v-else>
      <!-- 大数字卡 -->
      <div class="stats">
        <div class="stat-card"><div class="num">{{ data.total_count || 0 }}</div><div class="label">总篇数</div></div>
        <div class="stat-card"><div class="num">{{ formatWords(data.total_words) }}</div><div class="label">总字数</div></div>
        <div class="stat-card"><div class="num">{{ learnedDays }}</div><div class="label">学习天数</div></div>
        <div class="stat-card"><div class="num">{{ data.max_streak || 0 }}</div><div class="label">最长连续</div></div>
      </div>

      <!-- 年度高光 -->
      <div class="dist-wrap" v-if="hasAny">
        <div class="hm-title"><i class="ri-trophy-line"></i> 年度高光</div>
        <div class="year-highlights">
          <div class="hl-item">
            <i class="ri-calendar-check-line hl-icon"></i>
            <div class="hl-label">最勤奋月份</div>
            <div class="hl-value">{{ bestMonth || '-' }}</div>
          </div>
          <div class="hl-item">
            <i class="ri-price-tag-3-line hl-icon"></i>
            <div class="hl-label">最爱主题</div>
            <div class="hl-value">{{ topTopic || '-' }}</div>
          </div>
          <div class="hl-item">
            <i class="ri-fire-line hl-icon"></i>
            <div class="hl-label">最长连续</div>
            <div class="hl-value">{{ data.max_streak || 0 }} 天</div>
          </div>
        </div>
      </div>

      <!-- 全年热力图 -->
      <div class="hm-wrap" v-if="hasAny">
        <div class="hm-title"><i class="ri-calendar-check-line"></i> 全年热力图 <span class="hm-sub">{{ year }} · 学习 {{ learnedDays }} 天 · 共 {{ data.total_words || 0 }} 字</span></div>
        <div class="hm-body">
          <div class="hm-weekdays">
            <span class="hm-wd">一</span><span class="hm-wd"></span><span class="hm-wd">三</span><span class="hm-wd"></span><span class="hm-wd">五</span><span class="hm-wd"></span><span class="hm-wd"></span>
          </div>
          <div class="hm-grid">
            <div v-for="(w, wi) in heatWeeks" :key="'c' + wi" class="hm-col">
              <div
                v-for="d in w.days"
                :key="d.ymd"
                class="hm-cell"
                :class="[d.inYear ? 'lv' + d.level : 'hm-cell-out']"
                :title="d.inYear ? d.title : ''"
              ></div>
            </div>
          </div>
        </div>
        <div class="hm-legend">
          <span>少</span>
          <span class="hm-cell"></span>
          <span class="hm-cell lv1"></span>
          <span class="hm-cell lv2"></span>
          <span class="hm-cell lv3"></span>
          <span class="hm-cell lv4"></span>
          <span>多</span>
        </div>
      </div>

      <div v-if="!hasAny" class="empty"><i class="ri-inbox-line"></i> {{ year }} 年暂无学习记录</div>

      <!-- 页脚 -->
      <footer>
        <p>AI 学习笔记 · {{ year }} 年度总结</p>
      </footer>
    </template>
  </div>
</template>

<script>
import aiPush from '../api/aiPush'

// 热力图强度：与 HomePage 一致的按字数分级
function levelOf (words) {
  if (!words) return 0
  if (words < 600) return 1
  if (words < 1400) return 2
  if (words < 2400) return 3
  return 4
}

export default {
  name: 'YearPage',
  props: { year: { type: String, required: true } },
  data () {
    return {
      loading: false,
      error: '',
      data: {},
      yearList: [] // 当年全部条目（热力图用）
    }
  },
  computed: {
    hasAny () {
      return (this.data.total_count || 0) > 0
    },
    learnedDays () {
      return new Set(this.yearList.map(d => d.push_date)).size
    },
    bestMonth () {
      const list = this.data.month_words || []
      if (!list.length) return ''
      let best = list[0]
      for (const m of list) {
        if ((m.words || 0) > (best.words || 0)) best = m
      }
      return `${Number(best.month.slice(5))} 月（${best.count} 篇）`
    },
    topTopic () {
      const list = this.data.topic_dist || []
      if (!list.length) return ''
      const t = list[0] // 后端已按 count 倒序
      return `${t.topic_type || t.topic_code}（${t.count} 篇）`
    },
    /** 全年热力图：1月1日所在周 → 12月31日所在周，列=周 */
    heatWeeks () {
      const y = Number(this.year)
      if (!y) return []
      const map = {}
      this.yearList.forEach(d => {
        map[d.push_date] = { title: d.title, words: Number(d.word_count) || 0 }
      })
      const jan1 = new Date(y, 0, 1)
      const dec31 = new Date(y, 11, 31)
      const start = new Date(jan1)
      // 对齐到周一（getDay: 0=周日，转 0=周一）
      const dow = (start.getDay() + 6) % 7
      start.setDate(start.getDate() - dow)
      const weeks = []
      const cur = new Date(start)
      while (cur.getTime() <= dec31.getTime()) {
        const week = { days: [] }
        for (let i = 0; i < 7; i++) {
          const dt = new Date(cur)
          const inYear = dt.getFullYear() === y
          const ymd = this.formatYmd(dt)
          const info = map[ymd]
          let level = 0
          let title = ymd
          if (inYear && info) {
            level = levelOf(info.words)
            title = `${ymd} · ${info.title} · ${info.words} 字`
          }
          week.days.push({ ymd, level, title, inYear })
          cur.setDate(cur.getDate() + 1)
        }
        weeks.push(week)
      }
      return weeks
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      this.error = ''
      try {
        const y = Number(this.year)
        const [statsRes, listRes] = await Promise.all([
          aiPush.stats(y),
          aiPush.list({ startDate: `${y}-01-01`, endDate: `${y}-12-31`, size: 400 })
        ])
        this.data = statsRes.data || {}
        this.yearList = listRes.data || []
      } catch (e) {
        this.error = e.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    formatWords (n) {
      if (!n) return '0'
      if (n >= 10000) return (n / 10000).toFixed(1) + 'w'
      return String(n)
    },
    formatYmd (dt) {
      const m = String(dt.getMonth() + 1).padStart(2, '0')
      const d = String(dt.getDate()).padStart(2, '0')
      return `${dt.getFullYear()}-${m}-${d}`
    },
    goStats () {
      window.location.hash = '#/stats'
    },
    goHome () {
      window.location.hash = '#/notes'
    }
  }
}
</script>
